import { api, requestHandler } from "./requestHandler";

/** In-memory caches to avoid repeat HTTP calls for the same params (short TTL). */
const coinDetailCache = new Map();
const chartSeriesCache = new Map();

const COIN_DETAIL_TTL_MS = 5 * 60 * 1000;
const CHART_TTL_MS = 90 * 1000;
const MAX_CHART_CACHE = 48;

function trimChartCache() {
  while (chartSeriesCache.size > MAX_CHART_CACHE) {
    const firstKey = chartSeriesCache.keys().next().value;
    chartSeriesCache.delete(firstKey);
  }
}

function chartCacheKey(id, days, priceType) {
  return `${id}|${Number(days)}|${priceType}`;
}

export async function fetchCoins() {
  const { data, error } = await requestHandler(() =>
    api.get("/coins/markets", {
      params: {
        vs_currency: "usd",
        order: "market_cap_desc",
        sparkline: true,
      },
    })
  );
  if (error) {
    console.error("[cryptoService.fetchCoins]", error.message);
  }
  return data;
}

export async function getCoinData(id) {
  if (!id) return null;
  const now = Date.now();
  const cached = coinDetailCache.get(id);
  if (cached && cached.expires > now) {
    return cached.data;
  }

  const { data, error } = await requestHandler(() =>
    api.get(`/coins/${encodeURIComponent(id)}`)
  );
  if (error) {
    console.error("[cryptoService.getCoinData]", error.message);
    return null;
  }
  if (data) {
    coinDetailCache.set(id, { data, expires: now + COIN_DETAIL_TTL_MS });
  }
  return data;
}

function extractSeries(data, priceType) {
  if (priceType === "current_price") return data.prices;
  if (priceType === "market_cap") return data.market_caps;
  if (priceType === "total_volume") return data.total_volumes;
  return data.prices;
}

export async function getCoinPrices(id, days, priceType) {
  if (!id) return null;
  const dayCount = Number(days);
  const key = chartCacheKey(id, dayCount, priceType);
  const now = Date.now();
  const cached = chartSeriesCache.get(key);
  if (cached && cached.expires > now) {
    return cached.data;
  }

  const { data, error } = await requestHandler(() =>
    api.get(`/coins/${encodeURIComponent(id)}/market_chart`, {
      params: {
        vs_currency: "usd",
        days: dayCount,
      },
    })
  );
  if (error || !data) {
    if (error) console.error("[cryptoService.getCoinPrices]", error.message);
    return null;
  }

  const series = extractSeries(data, priceType);
  if (Array.isArray(series) && series.length > 0) {
    chartSeriesCache.set(key, { data: series, expires: now + CHART_TTL_MS });
    trimChartCache();
  }
  return series;
}
